import { AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { memo } from 'react';
import { Spinner } from '@/components/ui';

export interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: { value: number; isPositive: boolean };
  isLoading?: boolean;
  error?: boolean;
  className?: string;
}

/**
 * Reusable KPI/stat card component with label, value, optional trend indicator and loading/error states.
 * Follows the standard white card pattern with border and shadow used across the app.
 */
export const StatCard = memo(function StatCard({
  label,
  value,
  subtitle,
  trend,
  isLoading,
  error,
  className = '',
}: StatCardProps) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner size="lg" className="w-4 h-4 text-gray-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">Error loading</span>
        </div>
      ) : (
        <div>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>

          {trend && (
            <div
              className={`flex items-center gap-1 mt-2 text-xs font-medium ${
                trend.isPositive ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {trend.isPositive ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              <span>
                {Math.abs(trend.value)}% {trend.isPositive ? 'increase' : 'decrease'}
              </span>
            </div>
          )}

          {subtitle && <p className="text-xs text-gray-500 mt-2">{subtitle}</p>}
        </div>
      )}
    </div>
  );
});
